import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Search, People } from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { userService, type User, type UserRole } from "@/services/user.service";
import { CreateUserDialog } from "@/components/users/CreateUserDialog";
import { formatCustomerName } from "@/utils/formatCustomerName";

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: "Administrateur",
  MANAGER: "Manager",
  ORDER_CLERK: "Employé commandes",
  DRIVER: "Livreur",
};

export function UsersPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistedState("users.searchTerm", "");
  const [includeInactive, setIncludeInactive] = usePersistedState("users.includeInactive", true);

  const {
    data: users = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["users", searchTerm, includeInactive],
    queryFn: () =>
      userService.getAll(includeInactive, searchTerm || undefined),
  });

  if (isLoading) {
    return <LoadingSpinner message="Chargement des utilisateurs..." />;
  }

  if (error) {
    const err = error as {
      response?: { status?: number; data?: { error?: { message?: string }; message?: string } };
      message?: string;
      code?: string;
    };
    const apiMsg =
      err?.response?.data?.error?.message ?? err?.response?.data?.message;
    const status = err?.response?.status;
    const isNetworkOrEmpty =
      !err?.response && (err?.code === "ERR_NETWORK" || /empty|reset|failed/i.test(err?.message ?? ""));
    const fallback = isNetworkOrEmpty
      ? "Le serveur n'a pas répondu (connexion coupée ou backend arrêté). Vérifiez que le backend tourne et redémarrez-le si besoin."
      : status === 404
        ? "L'API utilisateurs (/api/users) n'est pas disponible. Vérifiez que le backend est à jour et redémarré."
        : "Erreur lors du chargement des utilisateurs.";
    const statusSuffix = status ? ` (HTTP ${status})` : "";
    const message = apiMsg
      ? `${apiMsg}${statusSuffix}`
      : (err?.message?.trim() || fallback);
    if (import.meta.env.DEV) {
      console.error("[UsersPage] Load error:", err);
    }
    return (
      <ErrorMessage
        title="Erreur de chargement"
        message={message}
        onRetry={() => refetch()}
        retryLabel="Réessayer"
      />
    );
  }

  const displayName = (u: User) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Utilisateurs"
        subtitle="Consultez et créez les comptes de l'application"
        action={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Créer un utilisateur
          </Button>
        }
      />

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} users={users} />

      <FiltersCard>
        <InputField
            placeholder="Rechercher par nom, prénom ou email..."
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
          />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          }
          label="Inclure les utilisateurs inactifs"
        />
      </FiltersCard>

      <Card padding="none">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Client (contact)</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Statut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <People sx={{ fontSize: 40, color: "text.secondary" }} />
                    <Typography color="text.secondary">
                      Aucun utilisateur trouvé
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {displayName(user)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.phone ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {user.customer ? (
                      <Typography
                        component={RouterLink}
                        to={`/customers/${user.customer.id}`}
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          fontWeight: 500,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {formatCustomerName(user.customer)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ROLE_LABELS[user.role] ?? user.role}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? "Actif" : "Inactif"}
                      color={user.isActive ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Card>

      {users.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {users.length} utilisateur{users.length > 1 ? "s" : ""} affiché
          {users.length > 1 ? "s" : ""}
        </Typography>
      )}
    </Box>
  );
}
